// Firestore reads and writes for the shared material library (see materialLibrary.ts).
import { collection, doc, runTransaction, serverTimestamp, setDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { PROJECT_FIELDS, planQuoteWrite, splitItemData, type MigrationPlan } from './materialLibrary';
import { ensureVendors } from './vendorStore';

export const MASTER_COLLECTION = 'materialLibrary';
export const LEGACY_COLLECTION = 'materialRateItems';

export const masterRef = (id: string) => doc(db, MASTER_COLLECTION, id);
export const projectRatesRef = (projectId: string, id: string) => doc(db, 'projects', projectId, 'materialRates', id);

/**
 * Saves vendor quotes (shared by every project) together with any per-project fields in one transaction.
 * `quotes` are the quotes as this project sees them; a freight or loading that differs from the shared
 * quote is kept as this project's own.
 */
async function saveQuotesAndRates(projectId: string, id: string, quotes: any[], masterPatch: Record<string, any>, projectPatch: Record<string, any>): Promise<void> {
  await runTransaction(db, async (tx) => {
    const [masterSnap, ratesSnap] = await Promise.all([tx.get(masterRef(id)), tx.get(projectRatesRef(projectId, id))]);
    if (!masterSnap.exists()) throw new Error('This material is no longer in the shared library.');
    const plan = planQuoteWrite(quotes, masterSnap.data() as Record<string, any>, ratesSnap.data() as Record<string, any> | undefined);
    tx.update(masterRef(id), { ...masterPatch, quotes: plan.quotes, updatedAt: serverTimestamp() });
    tx.set(projectRatesRef(projectId, id), { ...projectPatch, quoteOverrides: plan.overrides, updatedAt: serverTimestamp() }, { merge: true });
  });
  ensureVendors(projectId, [], quotes.map((quote) => quote?.vendor)).catch(() => undefined);
}

/** Saves changes to a material. Shared fields and quotes go to the shared library, the rest to this project's rates. */
export async function updateMaterialItem(projectId: string, id: string, patch: Record<string, any>): Promise<void> {
  const { master, project } = splitItemData(patch);
  if (Array.isArray(master.quotes)) {
    const { quotes, ...sharedFields } = master;
    return saveQuotesAndRates(projectId, id, quotes, sharedFields, project);
  }
  const batch = writeBatch(db);
  if (Object.keys(master).length) batch.update(masterRef(id), master);
  if (Object.keys(project).length) batch.set(projectRatesRef(projectId, id), project, { merge: true });
  await batch.commit();
}

/**
 * Creates a material in the shared library. This project's own choices (selected quote and so on) are
 * only written when given: a material with none needs no project document, and purchasing may only
 * write quotes there anyway.
 */
export async function createMaterialItem(projectId: string, data: Record<string, any>): Promise<string> {
  const ref = doc(collection(db, MASTER_COLLECTION));
  const { master, project } = splitItemData(data);
  const own = Object.fromEntries(Object.entries(project).filter(([key]) => (PROJECT_FIELDS as readonly string[]).includes(key) && key !== 'quoteOverrides'));
  const hasOwn = Object.values(own).some((value) => value !== null && !(typeof value === 'object' && Object.keys(value as object).length === 0));
  const batch = writeBatch(db);
  batch.set(ref, master);
  if (hasOwn) batch.set(projectRatesRef(projectId, ref.id), { ...own, updatedAt: serverTimestamp() });
  await batch.commit();
  return ref.id;
}

/** Changes this project's selections and notes for a material. Passing `quotes` saves them as the shared quotes. */
export async function setProjectRates(projectId: string, id: string, patch: Record<string, any>): Promise<void> {
  const { quotes, ...rest } = patch;
  if (Array.isArray(quotes)) return saveQuotesAndRates(projectId, id, quotes, {}, rest);
  await setDoc(projectRatesRef(projectId, id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}

/** Removes a material from the shared library (every project) and this project's rates for it. */
export async function deleteMaterialItem(projectId: string, id: string): Promise<void> {
  const batch = writeBatch(db);
  batch.delete(masterRef(id));
  batch.delete(projectRatesRef(projectId, id));
  await batch.commit();
}

/** Copies the planned materials into the shared library, keeping every id. Batches stay well under Firestore's limit. */
export async function runMigration(projectId: string, plan: MigrationPlan, onProgress?: (done: number, total: number) => void): Promise<void> {
  const size = 200;
  for (let start = 0; start < plan.create.length; start += size) {
    const batch = writeBatch(db);
    for (const entry of plan.create.slice(start, start + size)) {
      batch.set(masterRef(entry.id), { ...entry.master, migratedFromProject: projectId, migratedAt: serverTimestamp(), updatedAt: serverTimestamp() });
      batch.set(projectRatesRef(projectId, entry.id), { ...entry.project, updatedAt: serverTimestamp() });
    }
    await batch.commit();
    onProgress?.(Math.min(start + size, plan.create.length), plan.create.length);
  }
  // Vendors are shared by every project too; add the ones on the moved quotes.
  await ensureVendors(projectId, [], plan.vendors);
}
